import { useEffect, useRef, useState } from 'react';
import { Icon, type IconName } from '../ui/Icon';
import { Badge } from '../ui/Badge';
import { useTheme } from '../hooks/useTheme';
import { useToast } from '../ui/Toast';
import { BarMark } from './BarMark';

interface TopbarProps {
  onLogout?: () => void;
  userInitials?: string;
  userName?: string;
  papel?: 'super_admin' | 'partner_admin';
  onArea?: (id: string) => void;
}

type MenuKey = 'eco' | 'create' | 'news' | 'account';

interface Module {
  mark: string;
  name: string;
  desc: string;
  c: string;
  current?: boolean;
}

const MODULES: Module[] = [
  { mark: 'db', name: 'db · console', desc: 'Arquivos & providers', c: '--brand', current: true },
  { mark: 'ln', name: 'lente', desc: 'Visão computacional', c: '--lab-teal' },
  { mark: 'cf', name: 'cofre', desc: 'Chaves & acessos', c: '--lab-navy' },
  { mark: 'pl', name: 'pulso', desc: 'Observabilidade', c: '--amber' },
  { mark: 'ft', name: 'faturas', desc: 'Custos de IA', c: '--lab-purple' }
];

interface CreateItem {
  label: string;
  icon: IconName;
  dest: string;
  superOnly?: boolean;
}

const CREATE_ITEMS: CreateItem[] = [
  { label: 'Nova empresa', icon: 'building', dest: 'companies', superOnly: true },
  { label: 'Cadastrar provider', icon: 'plug', dest: 'providers', superOnly: true },
  { label: 'Revisar arquivos', icon: 'image', dest: 'files' }
];

const menuItem = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  width: '100%',
  padding: '8px 10px',
  borderRadius: 'var(--r-sm)',
  border: 'none',
  background: 'transparent',
  color: 'var(--text-1)',
  fontSize: 13,
  textAlign: 'left' as const,
  fontWeight: 500,
  cursor: 'pointer'
};

export function Topbar({
  onLogout,
  userInitials = 'A',
  userName,
  papel = 'super_admin',
  onArea
}: TopbarProps) {
  const { theme, toggle } = useTheme();
  const toast = useToast();
  const [open, setOpen] = useState<MenuKey | null>(null);
  const rootRef = useRef<HTMLElement>(null);

  // Fecha qualquer menu ao clicar fora ou Esc
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(null);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  function toggleMenu(k: MenuKey) {
    setOpen((cur) => (cur === k ? null : k));
  }

  const isSuper = papel === 'super_admin';
  const createItems = CREATE_ITEMS.filter((it) => !it.superOnly || isSuper);

  const iconBtn = {
    width: 36,
    height: 36,
    borderRadius: 'var(--r-sm)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: 'none',
    background: 'transparent',
    color: 'var(--topbar-fg-dim)',
    position: 'relative' as const,
    cursor: 'pointer'
  };

  function goCreate(dest: string) {
    onArea?.(dest);
    setOpen(null);
  }

  function openModule(m: Module) {
    if (!m.current) toast(`Abriria "${m.name}" — fora do escopo deste console`, 'info');
    setOpen(null);
  }

  function onSearchKey(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      toast('Busca global chega em breve — use os filtros de cada área.', 'info');
    }
  }

  return (
    <header
      ref={rootRef}
      style={{
        height: 'var(--topbar-h)',
        flexShrink: 0,
        background: 'var(--topbar-bg)',
        color: 'var(--topbar-fg)',
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        padding: '0 14px',
        zIndex: 60
      }}
    >
      {/* esquerda: ecosystem + marca */}
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 6 }}>
        <button
          onClick={() => toggleMenu('eco')}
          aria-label="Plataforma interna"
          aria-expanded={open === 'eco'}
          title="Plataforma interna"
          style={{ ...iconBtn, color: 'var(--topbar-fg)' }}
          onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--topbar-hover)')}
          onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
        >
          <Icon name="menu" size={19} />
        </button>
        <button
          onClick={() => toggleMenu('eco')}
          aria-label="db · console"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 9,
            background: 'transparent',
            border: 'none',
            padding: '4px 8px 4px 4px',
            borderRadius: 'var(--r-sm)',
            cursor: 'pointer'
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--topbar-hover)')}
          onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
        >
          <BarMark size={24} />
          <span
            style={{
              fontSize: 19,
              fontWeight: 800,
              letterSpacing: '-0.03em',
              color: 'var(--topbar-fg)'
            }}
          >
            db<span style={{ color: 'var(--brand)' }}>.</span>
          </span>
        </button>
        {open === 'eco' && (
          <div
            className="anim-scale"
            style={{
              position: 'absolute',
              left: 0,
              top: 'calc(100% + 8px)',
              width: 280,
              background: 'var(--bg-1)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--r-md)',
              boxShadow: 'var(--shadow-2)',
              padding: 8,
              zIndex: 70
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '4px 8px 10px'
              }}
            >
              <span className="eyebrow">Plataforma interna</span>
              <Badge size="sm" color="neutral" mono>
                5 módulos
              </Badge>
            </div>
            {MODULES.map((m) => (
              <button
                key={m.name}
                onClick={() => openModule(m)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  width: '100%',
                  padding: '9px 8px',
                  borderRadius: 'var(--r-sm)',
                  border: 'none',
                  background: m.current ? 'var(--brand-tint)' : 'transparent',
                  textAlign: 'left',
                  marginBottom: 2,
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  if (!m.current) e.currentTarget.style.background = 'var(--bg-2)';
                }}
                onMouseLeave={(e) => {
                  if (!m.current) e.currentTarget.style.background = 'transparent';
                }}
              >
                <span
                  style={{
                    width: 30,
                    height: 30,
                    borderRadius: 8,
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontFamily: 'var(--font-mono)',
                    fontSize: 12.5,
                    fontWeight: 700,
                    letterSpacing: '-0.04em',
                    background: `var(${m.c})`,
                    color: '#fff'
                  }}
                >
                  {m.mark}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-1)' }}>{m.name}</div>
                  <div style={{ fontSize: 11.5, color: 'var(--text-3)' }}>{m.desc}</div>
                </div>
                {m.current ? (
                  <Badge size="sm" color="success" dot>
                    atual
                  </Badge>
                ) : (
                  <Icon name="external" size={14} style={{ color: 'var(--text-3)' }} />
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* busca */}
      <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
        <div style={{ position: 'relative', width: '100%', maxWidth: 540 }}>
          <span
            style={{
              position: 'absolute',
              left: 12,
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'var(--text-3)',
              display: 'flex'
            }}
          >
            <Icon name="search" size={16} />
          </span>
          <input
            id="global-search"
            name="global-search"
            placeholder="Buscar arquivos, empresas, providers…"
            title="Busca global — em breve"
            onFocus={() => setOpen(null)}
            onKeyDown={onSearchKey}
            style={{
              width: '100%',
              height: 38,
              padding: '0 70px 0 36px',
              borderRadius: 99,
              border: '1px solid transparent',
              background: 'var(--search-bg)',
              color: 'var(--search-fg)',
              fontSize: 13.5,
              outline: 'none'
            }}
          />
          <span
            className="mono"
            style={{
              position: 'absolute',
              right: 10,
              top: '50%',
              transform: 'translateY(-50%)',
              fontSize: 11,
              color: 'var(--text-3)',
              border: '1px solid var(--border)',
              borderRadius: 5,
              padding: '2px 6px',
              background: 'var(--bg-2)'
            }}
          >
            ⌘K
          </span>
        </div>
      </div>

      {/* direita */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        {/* Criar */}
        {createItems.length > 0 && (
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => toggleMenu('create')}
              aria-label="Criar"
              aria-expanded={open === 'create'}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 7,
                height: 36,
                padding: '0 14px',
                borderRadius: 'var(--r-sm)',
                border: 'none',
                background: 'var(--amber)',
                color: 'oklch(0.28 0.04 70)',
                fontWeight: 700,
                fontSize: 13.5,
                cursor: 'pointer'
              }}
            >
              <Icon name="plus" size={16} /> Criar
            </button>
            {open === 'create' && (
              <div
                className="anim-scale"
                style={{
                  position: 'absolute',
                  right: 0,
                  top: 'calc(100% + 8px)',
                  width: 220,
                  background: 'var(--bg-1)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--r-md)',
                  boxShadow: 'var(--shadow-2)',
                  padding: 6,
                  zIndex: 70
                }}
              >
                {createItems.map((it) => (
                  <button
                    key={it.label}
                    onClick={() => goCreate(it.dest)}
                    style={menuItem}
                    onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg-2)')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                  >
                    <Icon name={it.icon} size={16} style={{ color: 'var(--text-2)' }} />
                    {it.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Novidades */}
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => toggleMenu('news')}
            aria-label="Novidades"
            aria-expanded={open === 'news'}
            title="Novidades"
            style={iconBtn}
            onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--topbar-hover)')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
          >
            <Icon name="activity" size={18} />
            <span
              style={{
                position: 'absolute',
                top: 7,
                right: 7,
                width: 7,
                height: 7,
                borderRadius: 99,
                background: 'var(--amber)',
                border: '1.5px solid var(--topbar-bg)'
              }}
            />
          </button>
          {open === 'news' && (
            <div
              className="anim-scale"
              style={{
                position: 'absolute',
                right: 0,
                top: 'calc(100% + 8px)',
                width: 260,
                background: 'var(--bg-1)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--r-md)',
                boxShadow: 'var(--shadow-2)',
                padding: 12,
                zIndex: 70
              }}
            >
              <div className="eyebrow" style={{ paddingBottom: 8 }}>Novidades</div>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 6,
                  padding: '14px 8px',
                  textAlign: 'center'
                }}
              >
                <Icon name="sparkles" size={20} style={{ color: 'var(--text-3)' }} />
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-1)' }}>
                  Nada novo por aqui
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-3)' }}>
                  O feed de novidades chega em breve.
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Tema */}
        <button
          onClick={toggle}
          aria-label={theme === 'dark' ? 'Mudar para tema claro' : 'Mudar para tema escuro'}
          title="Tema"
          style={iconBtn}
          onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--topbar-hover)')}
          onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
        >
          <Icon name={theme === 'dark' ? 'sun' : 'moon'} size={18} />
        </button>

        {/* Conta */}
        <div style={{ position: 'relative', marginLeft: 4 }}>
          <button
            onClick={() => toggleMenu('account')}
            aria-label="Menu da conta"
            aria-expanded={open === 'account'}
            title="Conta"
            style={{
              width: 34,
              height: 34,
              borderRadius: 99,
              border: '2px solid var(--topbar-hover)',
              background: 'linear-gradient(135deg, var(--amber), var(--amber-strong))',
              color: '#fff',
              fontSize: 12.5,
              fontWeight: 700,
              cursor: 'pointer'
            }}
          >
            {userInitials.toUpperCase()}
          </button>
          {open === 'account' && (
            <div
              className="anim-scale"
              style={{
                position: 'absolute',
                right: 0,
                top: 'calc(100% + 8px)',
                width: 230,
                background: 'var(--bg-1)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--r-md)',
                boxShadow: 'var(--shadow-2)',
                padding: 6,
                zIndex: 70
              }}
            >
              <div
                style={{
                  padding: '8px 10px 10px',
                  borderBottom: '1px solid var(--border-soft)',
                  marginBottom: 6
                }}
              >
                <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--text-1)' }}>
                  {userName ?? 'Conta'}
                </div>
                <div className="mono" style={{ fontSize: 11, color: 'var(--text-3)' }}>
                  {papel}
                </div>
                <div style={{ marginTop: 6 }}>
                  <Badge color={isSuper ? 'violet' : 'info'} size="sm">
                    {isSuper ? 'acesso total' : 'empresa'}
                  </Badge>
                </div>
              </div>
              <button
                onClick={() => {
                  setOpen(null);
                  onArea?.('account');
                }}
                style={menuItem}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg-2)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              >
                <Icon name="key" size={16} style={{ color: 'var(--text-2)' }} />
                Minha conta
              </button>
              <button
                onClick={() => {
                  setOpen(null);
                  onLogout?.();
                }}
                style={{ ...menuItem, color: 'var(--danger)' }}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg-2)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              >
                <Icon name="logout" size={16} />
                Sair
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
